/**
 * Vloeiend doorlopen bij scrollen met een muiswiel.
 *
 * Het thema scrolde al zonder haperen: gemeten op de homepage zit de mediaan
 * op 8ms per frame en komt geen enkel frame boven de 20ms. Dit gaat dus niet
 * over jank repareren, maar over gevoel. Een muiswiel scrollt in stappen van
 * ongeveer 100px; die stappen worden hier uitgesmeerd over een paar frames.
 *
 * Bewust beperkt tot het muiswiel:
 *
 * - Touch blijft ongemoeid. Telefoons en tablets hebben al momentum van het
 *   besturingssysteem en dat overnemen maakt het aantoonbaar slechter.
 * - Toetsenbord, zoeken-op-pagina, ankerlinks en het slepen van de scrollbar
 *   blijven native. Die gaan niet door de wheel-listener heen.
 * - Boven een element dat zelf kan scrollen doen we niets, anders kun je in de
 *   winkelwagenlade niet meer scrollen.
 *
 * Staat uit onder prefers-reduced-motion, onder de schakelaar "Animaties" in
 * de theme editor, en als de klant hem apart uitzet.
 */

// Deel van de resterende afstand dat elk frame wordt afgelegd. Hoger is
// directer. Op 0.14 duurde het ruim een halve seconde voor een wieltik was
// uitgelopen en dat voelde traag. Deze waarde komt op ongeveer 130ms uit:
// nog altijd acht frames interpolatie tegenover de ene sprong die de browser
// zelf maakt, maar de beweging is af voordat je hem als vertraging opmerkt.
const AFVLAKKING = 0.45;
const KLAAR = 0.5; // px, waaronder we stoppen en afronden
// Hoever het doel voor mag lopen op waar we nu staan, in wielstappen. Zonder
// deze grens spaart een snelle flick een berg afstand op die daarna nog
// seconden doorloopt terwijl de bezoeker al gestopt is.
const MAX_VOORSPRONG = 3;

class SoepelScrollen {
  constructor() {
    this.doel = window.scrollY;
    this.geschreven = window.scrollY;
    this.loopt = false;
    this.onWheel = this.onWheel.bind(this);
    this.stap = this.stap.bind(this);
    this.synchroniseer = this.synchroniseer.bind(this);
  }

  start() {
    window.addEventListener("wheel", this.onWheel, { passive: false });
    // Scrollt de bezoeker op een andere manier, dan is ons doel achterhaald.
    window.addEventListener("scroll", this.synchroniseer, { passive: true });
  }

  /**
   * Laat los zodra iemand anders de pagina verplaatst.
   *
   * Loopt er een afvlakking en staat de pagina ergens anders dan waar wij hem
   * net neerzetten, dan was het een ankerlink, de toetsenbordpijlen,
   * zoeken-op-pagina of de theme editor. Zonder dit trekt onze lus de pagina
   * daarna terug naar zijn eigen doel.
   *
   * Dit is een verfijning, geen fundament: het vertrekpunt van een nieuwe
   * wieltik komt in onWheel altijd vers uit window.scrollY, juist omdat je er
   * niet op mag rekenen dat elke verplaatsing een scroll-event oplevert.
   */
  synchroniseer() {
    if (!this.loopt) return;
    if (Math.abs(window.scrollY - this.geschreven) > 2) this.loopt = false;
  }

  /**
   * Kan een voorouder van dit element de scroll zelf opvangen?
   *
   * event.target hoeft geen Element te zijn: bij een wheel op lege ruimte of
   * op het document krijg je het document of window terug, en getComputedStyle
   * weigert die. Vandaar de typecheck vooraf.
   */
  static inEigenScroller(doel, richting) {
    let start = doel instanceof Element ? doel : null;

    for (let el = start; el && el !== document.body; el = el.parentElement) {
      const stijl = getComputedStyle(el);
      const scrollbaar = /(auto|scroll|overlay)/.test(
        stijl.overflowY + stijl.overflow,
      );
      if (!scrollbaar || el.scrollHeight <= el.clientHeight) continue;

      const ruimteOnder = el.scrollHeight - el.clientHeight - el.scrollTop;
      if (richting > 0 ? ruimteOnder > 1 : el.scrollTop > 1) return true;
    }
    return false;
  }

  onWheel(event) {
    // Ctrl of cmd erbij is zoomen, geen scrollen.
    if (event.ctrlKey || event.metaKey) return;
    // deltaMode 1 en 2 zijn regels en pagina's; die laten we met rust omdat de
    // omrekening naar pixels per browser verschilt.
    if (event.deltaMode !== 0) return;
    if (SoepelScrollen.inEigenScroller(event.target, event.deltaY)) return;

    const grens = document.documentElement.scrollHeight - window.innerHeight;
    if (grens <= 0) return;

    event.preventDefault();

    // Opsparen doen we alleen zolang er een afvlakking loopt. Staat die stil,
    // dan is de huidige positie het vertrekpunt en nooit een onthouden waarde:
    // die kan achterlopen als de pagina intussen op een andere manier is
    // verplaatst, en dan scrolt de eerste wieltik de verkeerde kant op.
    const basis = this.loopt ? this.doel : window.scrollY;

    // Eén op één met wat de browser zelf zou scrollen: dit verandert alleen
    // hoe de stap wordt uitgeserveerd, niet hoe ver een wieltik draagt.
    const voorsprong = MAX_VOORSPRONG * Math.abs(event.deltaY);
    const doel = basis + event.deltaY;

    this.doel = Math.min(
      Math.max(doel, 0, window.scrollY - voorsprong),
      grens,
      window.scrollY + voorsprong,
    );

    if (!this.loopt) {
      this.loopt = true;
      // Ijkpunt voor synchroniseer, zodat die niet met een oude waarde vergelijkt.
      this.geschreven = window.scrollY;
      requestAnimationFrame(this.stap);
    }
  }

  stap() {
    if (!this.loopt) return;

    const verschil = this.doel - window.scrollY;
    const klaar = Math.abs(verschil) < KLAAR;

    this.geschreven = klaar ? this.doel : window.scrollY + verschil * AFVLAKKING;

    // behavior "instant" is nodig: het thema zet scroll-behavior op smooth,
    // en dat zou onze eigen interpolatie nog eens overdoen.
    window.scrollTo({ top: this.geschreven, behavior: "instant" });

    if (klaar) {
      this.loopt = false;
      return;
    }
    requestAnimationFrame(this.stap);
  }
}

// De schakelaar in de theme editor zit op het script-tag in theme.liquid: staat
// hij uit, dan wordt dit bestand niet eens opgehaald. Hier blijven alleen de
// voorwaarden over die de browser zelf stelt.
const rustig = window.matchMedia("(prefers-reduced-motion: reduce)");
const muis = window.matchMedia("(pointer: fine)");

if (
  muis.matches &&
  !rustig.matches &&
  document.documentElement.dataset.animations !== "off"
) {
  new SoepelScrollen().start();
}
